import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import { formatLocalIsoDate, getWeekRange, isInWeek, localIsoDateFromInstant } from '../../lib/weekRange'
import {
  dateForDayInWeek,
  endTimeForNewStart,
  isRestDayBlock,
  normalizeTimeHHMM,
} from '../../lib/trainingSchedule'
import { inferWorkEvent } from '../../lib/calendarWorkEvent'
import type { TrainingBlock } from '../../lib/protocolTypes'

export type ExternalEvent = {
  id: string
  title: string
  starts_at: string
  ends_at: string
  is_work_event?: boolean
}

type WeeklyCalendarSheetProps = {
  open: boolean
  onClose: () => void
  training: TrainingBlock[]
  externalEvents: ExternalEvent[]
  completedScheduleIds?: string[]
  onSyncCalendar?: () => void
  calendarConnected: boolean
  onUpdateWorkoutTime?: (scheduleId: string, startTime: string, endTime: string) => Promise<void>
  savingScheduleId?: string | null
}

type KiloCalendarEvent = {
  id: string
  type: 'kilo'
  scheduleId: string
  title: string
  subtitle: string
  start: string
  end: string
  startTime: string
  endTime: string
  isRestDay: boolean
  completed: boolean
  editable: boolean
}

const WeeklyCalendarSheet = ({
  open,
  onClose,
  training,
  externalEvents,
  completedScheduleIds = [],
  onSyncCalendar,
  calendarConnected,
  onUpdateWorkoutTime,
  savingScheduleId = null,
}: WeeklyCalendarSheetProps) => {
  const { t, i18n } = useTranslation()
  const [hideWork, setHideWork] = useState(false)
  const [weekOffset, setWeekOffset] = useState(0)
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null)
  const [draftStartTime, setDraftStartTime] = useState('')
  const [editError, setEditError] = useState('')

  const weekLabel = getWeekRange(weekOffset).label

  const completedIds = useMemo(() => new Set(completedScheduleIds), [completedScheduleIds])

  const events = useMemo(() => {
    const kilo: KiloCalendarEvent[] = training
      .map((b) => {
        const scheduledDate = dateForDayInWeek(b.day_name_id, weekOffset)
        const restDay = isRestDayBlock(b)
        const completed = completedIds.has(b.id)
        const startTime = normalizeTimeHHMM(b.start_time)
        const endTime = normalizeTimeHHMM(b.end_time)
        return {
          id: b.id,
          type: 'kilo' as const,
          scheduleId: b.id,
          title: restDay ? t('calendar.weekly.restDay') : b.block_title,
          subtitle: restDay
            ? b.focus_subtitle || t('calendar.weekly.scheduledRecovery')
            : completed
              ? t('calendar.weekly.completedTraining')
              : b.focus_subtitle,
          start: `${scheduledDate}T${startTime}`,
          end: `${scheduledDate}T${endTime}`,
          startTime,
          endTime,
          isRestDay: restDay,
          completed,
          editable: !restDay && (b.exercises?.length ?? 0) > 0,
        }
      })
      .filter((b) => isInWeek(b.start, weekOffset))

    const external = externalEvents
      .filter((e) => isInWeek(e.starts_at, weekOffset))
      .filter((e) => !(hideWork && (e.is_work_event || inferWorkEvent(e.title))))
      .map((e) => ({
        id: e.id,
        type: 'external' as const,
        title: e.title,
        subtitle: e.is_work_event ? t('calendar.weekly.work') : t('calendar.weekly.external'),
        start: e.starts_at,
        end: e.ends_at,
      }))

    return [...kilo, ...external].sort((a, b) => a.start.localeCompare(b.start))
  }, [training, externalEvents, hideWork, weekOffset, t, completedIds])

  const groupedByDay = useMemo(() => {
    const { start } = getWeekRange(weekOffset)
    const eventsByDate = new Map<string, typeof events>()
    for (const ev of events) {
      const iso = localIsoDateFromInstant(ev.start)
      const list = eventsByDate.get(iso) ?? []
      list.push(ev)
      eventsByDate.set(iso, list)
    }

    return Array.from({ length: 7 }, (_, dayIndex) => {
      const d = new Date(start)
      d.setHours(12, 0, 0, 0)
      d.setDate(start.getDate() + dayIndex)
      const iso = formatLocalIsoDate(d)
      const dayKey = d.toLocaleDateString(i18n.language, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      })
      return [dayKey, eventsByDate.get(iso) ?? []] as const
    })
  }, [events, weekOffset, i18n.language])

  const beginEdit = (event: KiloCalendarEvent) => {
    setEditingScheduleId(event.scheduleId)
    setDraftStartTime(event.startTime)
    setEditError('')
  }

  const cancelEdit = () => {
    setEditingScheduleId(null)
    setDraftStartTime('')
    setEditError('')
  }

  const saveEdit = async (event: KiloCalendarEvent) => {
    if (!onUpdateWorkoutTime || !draftStartTime) return

    setEditError('')
    try {
      const nextEnd = endTimeForNewStart(event.startTime, event.endTime, draftStartTime)
      await onUpdateWorkoutTime(event.scheduleId, draftStartTime, nextEnd)
      cancelEdit()
    } catch (error) {
      setEditError(error instanceof Error ? error.message : t('calendar.weekly.timeUpdateError'))
    }
  }

  const formatTimeRange = (startIso: string, endIso: string) => {
    const startLabel = new Date(startIso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    const endLabel = new Date(endIso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    return `${startLabel} – ${endLabel}`
  }

  return (
    <BottomSheet variant="dark" open={open} onClose={onClose} title={t('calendar.weekly.title')}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="h-10 rounded-full border border-neutral-800 px-3 text-sm font-medium text-neutral-300" onClick={() => setWeekOffset((w) => w - 1)}>
            {t('calendar.weekly.prev')}
          </button>
          <p className="text-sm font-semibold text-white">{weekLabel}</p>
          <button type="button" className="h-10 rounded-full border border-neutral-800 px-3 text-sm font-medium text-neutral-300" onClick={() => setWeekOffset((w) => w + 1)}>
            {t('calendar.weekly.next')}
          </button>
        </div>

        {!calendarConnected ? (
          <div className="rounded-2xl border border-dashed border-neutral-800 bg-kilo-alt p-4 text-sm text-neutral-400">
            <p>{t('calendar.weekly.connectPrompt')}</p>
            <button type="button" className="group mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black" onClick={onSyncCalendar}>
              <MaterialIcon name="calendar_add_on" hoverable size="sm" />
              {t('calendar.weekly.syncCalendar')}
            </button>
          </div>
        ) : (
          <p className="text-xs text-neutral-500">{t('calendar.weekly.timeEditHint')}</p>
        )}

        <button
          type="button"
          className="sticky top-0 z-10 ml-auto flex h-10 items-center rounded-full border border-neutral-800 bg-kilo-card px-3 text-xs font-medium text-neutral-400"
          onClick={() => setHideWork((v) => !v)}
        >
          {hideWork ? t('calendar.weekly.showWorkEvents') : t('calendar.weekly.hideWorkEvents')}
        </button>

        {editError ? <p className="text-sm text-red-400">{editError}</p> : null}

        <div className="space-y-4">
          {events.length === 0 ? (
            <p className={`text-sm ${KILO.text.muted}`}>{t('calendar.weekly.noEvents')}</p>
          ) : null}
          {groupedByDay.map(([day, dayEvents]) => (
              <section key={day}>
                <p className={`mb-2 ${KILO.meta}`}>{day}</p>
                <div className="space-y-2">
                  {dayEvents.map((ev) => {
                    const isKilo = ev.type === 'kilo'
                    const kiloEvent = isKilo ? (ev as KiloCalendarEvent) : null
                    const isEditing = isKilo && editingScheduleId === kiloEvent?.scheduleId
                    const isSaving = isKilo && savingScheduleId === kiloEvent?.scheduleId

                    return (
                      <div
                        key={`${ev.type}-${ev.id}`}
                        className={`rounded-2xl border p-3 ${
                          isKilo
                            ? kiloEvent?.isRestDay
                              ? 'border-neutral-700 bg-neutral-900/80 text-neutral-400'
                              : kiloEvent?.completed
                                ? 'border-sky-500/40 bg-sky-500/10 text-white'
                                : 'border-kilo-accent/30 bg-kilo-accent/5 text-white'
                            : 'border-neutral-800 bg-neutral-900 text-neutral-400'
                        }`}
                      >
                        <p className="text-sm font-semibold">{ev.title}</p>
                        <p className="text-xs opacity-80">{ev.subtitle}</p>
                        <p className="mt-1 text-xs">{formatTimeRange(ev.start, ev.end)}</p>

                        {isKilo && kiloEvent?.editable && onUpdateWorkoutTime ? (
                          isEditing ? (
                            <div className="mt-3 space-y-2">
                              <label className="block space-y-1">
                                <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                                  {t('calendar.weekly.newStartTime')}
                                </span>
                                <input
                                  type="time"
                                  value={draftStartTime}
                                  onChange={(event) => setDraftStartTime(event.target.value)}
                                  className="h-11 w-full rounded-xl border border-neutral-800 bg-neutral-950 px-3 text-sm text-white"
                                />
                              </label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={Boolean(isSaving)}
                                  className="flex h-10 flex-1 items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
                                  onClick={() => void saveEdit(kiloEvent)}
                                >
                                  {isSaving ? t('calendar.weekly.savingTime') : t('calendar.weekly.saveTime')}
                                </button>
                                <button
                                  type="button"
                                  disabled={Boolean(isSaving)}
                                  className="h-10 rounded-xl border border-neutral-800 px-4 text-sm text-neutral-300"
                                  onClick={cancelEdit}
                                >
                                  {t('calendar.weekly.cancelEdit')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="mt-3 flex h-9 items-center gap-1.5 rounded-lg border border-neutral-800 px-3 text-xs font-medium text-neutral-300"
                              onClick={() => beginEdit(kiloEvent)}
                            >
                              <MaterialIcon name="schedule" size="sm" />
                              {t('calendar.weekly.changeTime')}
                            </button>
                          )
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </section>
            ))}
        </div>
      </div>
    </BottomSheet>
  )
}

export default WeeklyCalendarSheet
